/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Backbone from 'backbone';
import Ladda from 'ladda';
import Syphon from 'backbone.syphon';

import { validateForm } from '@/util/ParsleyUtils';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import UpdateUserProfile from '../../../administration/services/UpdateUserProfile';
import UpdateUserPassword from '../../services/UpdateUserPassword';
import ProfileFormView from '../../views/ProfileFormView/ProfileFormView';
import AddCommandView from '../../../login/views/AddCommandView/AddCommandView';

import tmplExtProfilePage from './ExtProfilePage.hbs';

const ExtProfilePage = View.extend({
  template: tmplExtProfilePage,

  className: 'ext-profile-page container-fluid',

  ui: {
    profileInfoForm: '.profile-info-form',
    addCommandView: '.add-command-view',
    save: '.btn-save'
  },

  regions: {
    profileInfoForm: '@ui.profileInfoForm',
    addCommandView: '@ui.addCommandView'
  },

  events: {
    'click @ui.save': 'saveCustomerProfile'
  },

  childViewEvents: {
    'passwordFields:shown': 'togglePasswordFields',
    'passwordFields:hidden': 'togglePasswordFields'
  },

  onRender() {
    this.showChildView(
      'profileInfoForm',
      new ProfileFormView({
        model: new Backbone.Model({
          profile: this.model.get('profile')
        })
      })
    );
    this.showChildView(
      'addCommandView',
      new AddCommandView({
        model: new Backbone.Model({
          externalUserCommands: this.model.get('externalUserCommands'),
          customerID: this.model.get('profile').id,
          isProfilePage: true
        })
      })
    );

    this.passwordFieldsShown = false;
  },

  togglePasswordFields(childView) {
    this.passwordFieldsShown = !this.passwordFieldsShown;
  },

  async saveCustomerProfile(e) {
    e.preventDefault();

    const spinner = Ladda.create(this.ui.save[0]);
    spinner.start();
    const formData = Syphon.serialize(this);
    formData.id = this.model.get('profile').id;
    formData.otdsUserId = this.model.get('profile').otdsUserId;
    formData.isExternal = '1';
    formData.isAuthorized = '1';

    try {
      //Validate form
      await validateForm(this.ui.profileInfoForm);
      const validCommandList = await this.getChildView('addCommandView').validateCommandsList();
      if (!validCommandList) return;
      this.toggleForm(false);

      await UpdateUserProfile(formData)

      //Reset Password
      if (formData.oldPassword === formData.newPassword && this.passwordFieldsShown) {
        this.showProfileError('SAME_PASSWORD_ERROR');
        return;
      } else if (formData.oldPassword !== '' && formData.newPassword !== '') {
        await UpdateUserPassword(formData);
      }

      //Update User's commands
      await this.getChildView('addCommandView').submit();

      this.showProfileSuccess();
    } catch (err) {
      if (err) {
        this.showProfileError(err?.message?.string);
      }
    } finally {
      spinner.stop();
      this.toggleForm(true);
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
    this.getChildView('profileInfoForm').toggleForm(state);
    this.getChildView('addCommandView').toggleForm(state);
  },

  showProfileSuccess() {
    showSuccessMessage({
      code: 'submissionSuccess',
      title: 'Profile Updated',
      message: 'Your profile has been updated.'
    });
  },

  showProfileError(err) {
    switch (err) {
      case 'INVALID_CREDENTIALS':
        showErrorMessage({
          errorCode: '401',
          errorTitle: 'Invalid Credentials',
          errorMessage: 'The current password you entered is incorrect. Please try again.'
        });
        break;
      case 'PASSWORD_ERROR':
        showErrorMessage({
          errorCode: '500',
          errorTitle: 'Password Reset Error',
          errorMessage: 'An error occurred while trying to update your password. Please try again or contact support.'
        });
        break;
      case 'SAME_PASSWORD_ERROR':
        showErrorMessage({
          errorCode: '401',
          errorTitle: 'Password Reset Error',
          errorMessage:
            'The current and new passwords entered are the same. Please enter a different password as your new password.'
        });
        break;
      default:
        showErrorMessage({
          errorCode: '500',
          errorTitle: 'Submission Error',
          errorMessage:
            'An error occurred while saving your profile. Try again or contact an administrator if your issue persists.'
        });
    }
  }
});

export default ExtProfilePage;
